import { ref } from "valtio";

import { IS_NODE } from "@/__main__/constants.mjs";
import getData from "@/__main__/get-data.mjs";
import router from "@/__main__/router.mjs";
import {
  ADS_BLACKLIST_CONFIG,
  ADS_STATIC_CONFIG,
} from "@/feature-ads/constants/constants.mjs";
import AdsPathBlacklist from "@/feature-ads/data-model-ads-blacklist.mjs";
import type { AdsConfig } from "@/feature-ads/data-model-ads-static.mjs";
import AdsStatic from "@/feature-ads/data-model-ads-static.mjs";
import adsRefs from "@/feature-ads/refs.mjs";
import { promiseTimeout } from "@/feature-ads/util/promise-timeout.mjs";
import { devError } from "@/util/dev.mjs";
import { fetchCountryCode } from "@/util/use-country-code.mjs";

// skipSafetyCheck: we are exempt from safety checks here, since these calls
// should fire w/o user interaction / navigation
export default async function fetchRemoteAdConfig(): Promise<AdsConfig> {
  if (IS_NODE) return AdsStatic.default;
  try {
    await promiseTimeout(fetchCountryCode({ skipSafetyCheck: true }), 5000);
    const adConfig = getData(
      ADS_STATIC_CONFIG,
      AdsStatic,
      ["volatile", "adsRemoteConfig"],
      {
        shouldSkipDBRead: true,
        skipSafetyCheck: true,
      },
    );
    const blacklistedPathsConfig = getData(
      ADS_BLACKLIST_CONFIG,
      AdsPathBlacklist,
      undefined,
      {
        shouldSkipDBRead: true,
        skipSafetyCheck: true,
      },
    );
    adsRefs.remoteConfig = ref(await adConfig);
    adsRefs.BLACKLISTED_PATHS = ref(
      await blacklistedPathsConfig
        .then((config) => config.paths)
        .catch(() => []),
    );

    // remote config to blacklist paths
    adsRefs.shouldShowAds.push(() => {
      const { route } = router;
      return !adsRefs.BLACKLISTED_PATHS.some((path) =>
        route.currentPath.startsWith(path),
      );
    });

    return adConfig;
  } catch (e) {
    devError("Failed to fetch remote ad config", e);
    return AdsStatic.default;
  }
}
